import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { AppError, ErrorCode } from '@/types/errors.ts';

interface State {
  remoteStream: MediaStream | null;
  iceCandidates: Array<RTCIceCandidate>;
  iceConnectionState?: RTCIceConnectionState;
  signalingState: {
    prev?: RTCSignalingState;
    curr: RTCSignalingState;
  };
  webRtcConnectionState?: 'connected' | 'disconnected' | 'connecting' | 'new';
  iceGatheringState?: RTCIceGatheringState;
  error: AppError | null;
  isPolite: boolean;
  pc: RTCPeerConnection | null;
  dataChannelDiscoveryHandlers: Array<(e: RTCDataChannelEvent) => any>;
  dataChannelHandlers: Map<string, {
    label: string,
    handlers: {
      onOpen: (dataChannel: RTCDataChannel, event: Event) => any;
      onMessage?: (event: MessageEvent) => any;
      onClose?: (event: Event) => any;
    },
    options?: RTCDataChannelInit
  }>;
}

const initialState: State = {
  remoteStream: null,
  iceCandidates: [],
  signalingState: {
    curr: 'stable'
  },
  error: null,
  isPolite: false,
  pc: null,
  dataChannelDiscoveryHandlers: [],
  dataChannelHandlers: new Map()
};

function reducer(state: State, action: any): State {
  switch (action.type) {
    case 'SET_REMOTE_STREAM':
      console.debug('useWebRTC::Remote stream set');
      return { ...state, remoteStream: action.payload };
    case 'SET_SIGNALING_STATE': {
      const { prev, curr } = action.payload;
      return {
        ...state, signalingState: {
          prev: prev ? prev : state.signalingState.curr, curr
        }
      };
    }
    case 'ADD_ICE_CANDIDATE':
      return { ...state, iceCandidates: [ ...state.iceCandidates, action.payload ] };
    case 'SET_ICE_CONNECTION_STATE':
      return { ...state, iceConnectionState: action.payload };
    case 'SET_RTC_CONNECTION_STATE':
      return { ...state, webRtcConnectionState: action.payload };
    case 'SET_ICE_GATHERING_STATE':
      return { ...state, iceGatheringState: action.payload };
    case 'SET_RTC_CONNECTION_ERROR':
      return state.error ? state : { ...state, error: { code: ErrorCode.RTCConnectionFailed } };
    case 'SET_POLITE':
      return { ...state, isPolite: true };
    case 'SET_PC':
      return { ...state, pc: action.payload, webRtcConnectionState: 'new' };
    case 'ADD_DATA_CHANNEL_DISCOVERY_HANDLER':
      return { ...state, dataChannelDiscoveryHandlers: [ ...state.dataChannelDiscoveryHandlers, action.payload ] };
    case 'RESET':
      if (state.pc) {
        console.info('Cleaning RTC connection...');
        state.pc.ontrack = null;
        state.pc.onicecandidate = null;
        state.pc.oniceconnectionstatechange = null;
        state.pc.onsignalingstatechange = null;
        state.pc.onicegatheringstatechange = null;
        state.pc.onnegotiationneeded = null;
        state.pc.onconnectionstatechange = null;
        state.pc.ondatachannel = null;
        state.pc.close();
      }
      return { ...initialState, dataChannelDiscoveryHandlers: state.dataChannelDiscoveryHandlers };
    default:
      return state;
  }
}

interface RtcConnectionInputType {
  iceConfiguration: RTCConfiguration | null;
  onCandidateDiscovery: (candidate: RTCIceCandidate) => void;
  onOfferSet: (description: RTCSessionDescription | null) => void; // callback effect when the offer is set
  onAnswerSet: (description: RTCSessionDescription | null) => void; // callback effect when the answer is set
}

const useWebRTC = (
  {
    iceConfiguration,
    onCandidateDiscovery,
    onOfferSet,
    onAnswerSet
  }: RtcConnectionInputType
) => {
  const [ {
    dataChannelHandlers,
    pc,
    isPolite,
    remoteStream,
    dataChannelDiscoveryHandlers,
    webRtcConnectionState,
    error,
    signalingState
  }, dispatch ] = useReducer(reducer, initialState);

  const makingOfferRef = useRef(false); // need this to be as a ref
  const ignoreOfferRef = useRef(false);
  const isSettingRemoteAnswerPendingRef = useRef(false);

  const [ resetRtcConnection, setResetRtcConnection ] = useState<boolean>(false);

  const cleanRtcConnection = useCallback(() => {
    makingOfferRef.current = false;
    ignoreOfferRef.current = false;
    isSettingRemoteAnswerPendingRef.current = false;
    dispatch({ type: 'RESET' });
  }, []);

  const createNewRtcConnection = useCallback((iceConfig: RTCConfiguration) => {
    const newPeerConnection = new RTCPeerConnection(iceConfig);

    // Setup event handlers
    newPeerConnection.onnegotiationneeded = async () => {
      try {
        console.debug('Negotiation needed triggered');
        makingOfferRef.current = true;
        // here it's only an offer creation as negotiation only fires in stable state
        // either an answer to the most recent offer from the remote peer or  a freshly-created offer if there's no negotiation underway

        const timeout = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error('Setting local description took too long'));
          }, 2000);
        });

        if (!newPeerConnection) {
          throw new Error('Rtc connection is null');
        }

        await Promise.race([
          newPeerConnection.setLocalDescription(),
          timeout
        ]);

        console.info('Local description (offer) set');
        console.debug('ICE gathering state:', newPeerConnection.iceGatheringState);

        onOfferSet(newPeerConnection.localDescription);

      } catch (err) {
        console.error('Failed to set local description or it timed out:', err);
        dispatch({
          type: 'SET_RTC_CONNECTION_ERROR',
          payload: { type: 'rtcError', message: err instanceof Error ? err.message : 'Unknown error' }
        });
      } finally {
        makingOfferRef.current = false;
      }
    };

    newPeerConnection.onicecandidate = ({ candidate }) => {
      if (!candidate) {
        console.info('Candidate gathering completed');
        return;
      }
      onCandidateDiscovery(candidate);
      dispatch({ type: 'ADD_ICE_CANDIDATE', payload: candidate });
    };

    newPeerConnection.oniceconnectionstatechange = () => {
      if (!newPeerConnection) {
        console.error('RTC connection is null');
        dispatch({
          type: 'SET_RTC_CONNECTION_ERROR',
          payload: { type: 'rtcError', message: 'RTC connection is null from oniceconnectionstatechange' }
        });
        return;
      }

      dispatch({ type: 'SET_ICE_CONNECTION_STATE', payload: newPeerConnection.iceConnectionState });
    };

    newPeerConnection.ontrack = ({ streams }) => {
      dispatch({ type: 'SET_REMOTE_STREAM', payload: streams[0] });
    };

    newPeerConnection.ondatachannel = (event: RTCDataChannelEvent) => {
      dataChannelDiscoveryHandlers.forEach(handler => {
        try {
          handler(event);
        } catch (err) {
          console.error('Error in data channel discovery handler');
          dispatch({
            type: 'SET_RTC_CONNECTION_ERROR',
            payload: {
              type: 'rtcError',
              message: err instanceof Error ? err.message : 'Error in data channel discovery handler'
            }
          });
        }
      });
    };

    newPeerConnection.onicegatheringstatechange = () => {
      if (!newPeerConnection) {
        console.error('RTC connection is null');
        dispatch({
          type: 'SET_RTC_CONNECTION_ERROR',
          payload: { type: 'rtcError', message: 'RTC connection is null onconnectionstatechange' }
        });
        return;
      }
      dispatch({ type: 'SET_ICE_GATHERING_STATE', payload: newPeerConnection.iceGatheringState });
    };

    newPeerConnection.onconnectionstatechange = () => {
      if (!newPeerConnection) {
        console.error('RTC connection is null');
        dispatch({
          type: 'SET_RTC_CONNECTION_ERROR',
          payload: { type: 'rtcError', message: 'RTC connection is null onconnectionstatechange' }
        });
        return;
      }

      switch (newPeerConnection.connectionState) {
        case 'connected':
          dispatch({ type: 'SET_RTC_CONNECTION_STATE', payload: 'connected' });
          break;
        case 'new':
          dispatch({ type: 'SET_RTC_CONNECTION_STATE', payload: 'new' });
          break;
        case 'connecting':
          dispatch({ type: 'SET_RTC_CONNECTION_STATE', payload: 'connecting' });
          break;
        case 'disconnected':
        case 'failed':
        case 'closed':
          dispatch({ type: 'SET_RTC_CONNECTION_STATE', payload: 'disconnected' });
          break;
      }
    };

    dispatch({ type: 'SET_PC', payload: newPeerConnection });
  }, []);

  // Handle incoming signals
  const handleSignal = useCallback(async ({ description, candidate }: any) => {
    if (!pc) {
      console.error('RTC connection is null');
      dispatch({
        type: 'SET_RTC_CONNECTION_ERROR',
        payload: { type: 'rtcError', message: 'RTC connection is null from handleSignal' }
      });
      return;
    }

    try {
      if (description) {
        const readyForOffer =
          !makingOfferRef.current &&
          (pc.signalingState === 'stable' || isSettingRemoteAnswerPendingRef.current);

        const collision = description.type === 'offer' && !readyForOffer;

        ignoreOfferRef.current = !isPolite && collision; // impolite and collision -> ignore offer
        if (ignoreOfferRef.current) return;

        // answer or offer with polite
        isSettingRemoteAnswerPendingRef.current = description.type === 'answer';

        // stable -> have remote offer(no collision) or have remote offer(polite)
        // have-local-offer -> stable(have remote answer)
        await pc.setRemoteDescription(description); // polite/impolite and no collision -> set remote description
        console.log('Remote description set');

        isSettingRemoteAnswerPendingRef.current = false;

        if (description.type === 'offer') {
          await pc.setLocalDescription(); // set local answer [have-remote-offer -> stable]
          // console.debug('Local description (answer) set', pc.localDescription);
          onAnswerSet(pc.localDescription);
        }
      } else if (candidate) {
        try {
          await pc.addIceCandidate(candidate);
        } catch (err) {
          // if (!ignoreOfferRef.current) throw err; // todo:
        }
      }
    } catch (err) {
      console.error('Failed to set remote description or add ICE candidate:', err);
      dispatch({
        type: 'SET_RTC_CONNECTION_ERROR',
        payload: {
          type: 'rtcError',
          message: err instanceof Error ? err.message : 'Failed to set remote description or add ICE candidate'
        }
      });
    }
  }, [ pc, isPolite ]);

  // public api
  const createDataChannel = (label: string, handlers: {
    onOpen: (dataChannel: RTCDataChannel, event: Event) => any; // this is needed to notify upon channel creation
    onMessage?: (event: MessageEvent) => any;
    onClose?: (event: Event) => any;
  }, options?: RTCDataChannelInit) => {
    dataChannelHandlers.set(label, { label, handlers, options });
  };

  const dataChannelOnDiscovery = (handler: (event: RTCDataChannelEvent) => any) => {
    dispatch({ type: 'ADD_DATA_CHANNEL_DISCOVERY_HANDLER', payload: handler });
  };

  const setPolite = () => {
    dispatch({ type: 'SET_POLITE' });
  };

  const addLocalTracks = useCallback((stream: MediaStream) => {
    // negotiation needed is triggered here
    if (!pc) {
      console.error('RTC connection is null');
      dispatch({
        type: 'SET_RTC_CONNECTION_ERROR',
        payload: { type: 'rtcError', message: 'RTC connection is null from addLocalTracks' }
      });
      return;
    }

    stream
      .getTracks()
      .forEach((track) => pc.addTrack(track, stream));

    // setup data channels to be included in one negotiation
    dataChannelHandlers.forEach(({ label, handlers, options }) => {
      const dataChannel = pc.createDataChannel(label, options); // can use state.pc? as we are sure that state.pc exists here
      if (!dataChannel) return; // always data channel should be created.

      console.log(`data channel created ${label}`);

      dataChannel.onopen = (event) => {
        handlers.onOpen(dataChannel, event); // returns data channel
      };

      if (handlers.onMessage) dataChannel.onmessage = handlers.onMessage;
      if (handlers.onClose) dataChannel.onclose = handlers.onClose;
    });
    dataChannelHandlers.clear();
  }, [ dataChannelHandlers, pc ]);

  const reset = () => {
    setResetRtcConnection(true);
  };

  const initializeRtcConnection = useCallback(() => {
    if (!iceConfiguration) {
      console.warn('iceConfiguration not set. skipping RTC connection initialization');
      return;
    }

    cleanRtcConnection();

    try {
      createNewRtcConnection(iceConfiguration);
    } catch (err) {
      // Handle DOMException: Failed to construct 'RTCPeerConnection': when the network is down
      console.error('Failed to create RTCPeerConnection:', err);
      dispatch({
        type: 'SET_RTC_CONNECTION_ERROR',
        payload: {
          type: 'network',
          message: err instanceof Error ? err.message : 'Unable to establish a connection to the chat server'
        }
      });
    }

  }, [ iceConfiguration, createNewRtcConnection, cleanRtcConnection, dispatch ]);

  useEffect(() => {
    initializeRtcConnection();
  }, [ iceConfiguration ]);

  useEffect(() => {
    if (!resetRtcConnection) return;

    initializeRtcConnection();

    setResetRtcConnection(false);
  }, [ resetRtcConnection ]);

  return {
    remoteStream: remoteStream,
    rtcConnectionState: webRtcConnectionState,
    error,
    localDescription: pc?.localDescription,
    signalingState,
    handleSignal,
    createDataChannel,
    dataChannelOnDiscovery,
    reset,
    setPolite,
    addLocalTracks
  };
};

export { useWebRTC };
